(() => {
  if (window.SabhaThemeLoaded) return;
  window.SabhaThemeLoaded = true;

  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer: fine)').matches;
  const wishlistKey = 'sabha-shopify-wishlist-v1';
  let revealObserver;
  let searchTimer;
  let scrollTicking = false;

  if (!reducedMotion) document.documentElement.classList.add('motion-ready');

  function escapeHtml(value = '') {
    return String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    })[character]);
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(amount);
    } catch {
      return '$' + amount.toFixed(2);
    }
  }

  function setOpen(element, open) {
    if (!element) return;
    element.classList.toggle('is-open', open);
    element.setAttribute('aria-hidden', String(!open));
    q('.drawer-backdrop')?.classList.toggle('is-open', open);
    document.body.classList.toggle('drawer-open', open);
  }

  function closePanels() {
    qa('.side-drawer,.search-panel,.mobile-nav').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    q('.drawer-backdrop')?.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
  }

  class ProductCarousel {
    constructor(root) {
      this.root = root;
      this.slides = qa('.carousel-slide', root);
      this.dots = qa('.carousel-dots button', root);
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.paused = false;
      this.startX = null;
      this.interval = null;

      if (this.slides.length < 2) return;

      this.previousButton = q('.carousel-arrow--prev', root);
      this.nextButton = q('.carousel-arrow--next', root);
      this.previousHandler = () => this.move(-1);
      this.nextHandler = () => this.move(1);
      this.previousButton?.addEventListener('click', this.previousHandler);
      this.nextButton?.addEventListener('click', this.nextHandler);

      this.dotHandlers = this.dots.map((dot, index) => {
        const handler = () => this.show(index, index >= this.index ? 1 : -1);
        dot.addEventListener('click', handler);
        return handler;
      });

      this.mouseEnterHandler = () => { this.paused = true; };
      this.mouseLeaveHandler = () => { this.paused = false; };
      this.focusInHandler = () => { this.paused = true; };
      this.focusOutHandler = (event) => { if (!root.contains(event.relatedTarget)) this.paused = false; };
      this.touchStartHandler = (event) => {
        this.paused = true;
        this.startX = event.touches[0]?.clientX ?? null;
      };
      this.touchEndHandler = (event) => {
        const endX = event.changedTouches[0]?.clientX;
        if (this.startX != null && endX != null && Math.abs(endX - this.startX) > 42) {
          this.move(endX < this.startX ? 1 : -1);
        }
        this.startX = null;
        this.paused = false;
      };

      root.addEventListener('mouseenter', this.mouseEnterHandler);
      root.addEventListener('mouseleave', this.mouseLeaveHandler);
      root.addEventListener('focusin', this.focusInHandler);
      root.addEventListener('focusout', this.focusOutHandler);
      root.addEventListener('touchstart', this.touchStartHandler, { passive: true });
      root.addEventListener('touchend', this.touchEndHandler, { passive: true });

      if (root.dataset.autoplay === 'true' && !reducedMotion) {
        const seconds = Math.max(3, Math.min(6, Number(root.dataset.delay || 4)));
        this.interval = window.setInterval(() => {
          if (!this.paused && !document.hidden && root.isConnected) this.move(1);
        }, seconds * 1000);
      }
    }

    move(change) {
      this.show((this.index + change + this.slides.length) % this.slides.length, change);
    }

    show(index, direction = 1) {
      if (index === this.index || !this.slides[index]) return;
      this.root.classList.toggle('is-previous', direction < 0);
      this.index = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const count = q('.carousel-count', this.root);
      if (count) count.textContent = `${index + 1} / ${this.slides.length}`;
    }

    destroy() {
      if (this.interval) window.clearInterval(this.interval);
      this.previousButton?.removeEventListener('click', this.previousHandler);
      this.nextButton?.removeEventListener('click', this.nextHandler);
      this.dots.forEach((dot, index) => dot.removeEventListener('click', this.dotHandlers[index]));
      this.root.removeEventListener('mouseenter', this.mouseEnterHandler);
      this.root.removeEventListener('mouseleave', this.mouseLeaveHandler);
      this.root.removeEventListener('focusin', this.focusInHandler);
      this.root.removeEventListener('focusout', this.focusOutHandler);
      this.root.removeEventListener('touchstart', this.touchStartHandler);
      this.root.removeEventListener('touchend', this.touchEndHandler);
      delete this.root.sabhaCarousel;
    }
  }

  function initCarousels(root = document) {
    qa('[data-carousel]', root).forEach((carousel) => {
      if (carousel.sabhaCarousel) return;
      carousel.sabhaCarousel = new ProductCarousel(carousel);
    });
  }

  function initReveal(root = document) {
    const selector = [
      '.section-heading', '.promise-bar', '.filter-row', '.product-card', '.craft__image', '.craft__copy',
      '.product-main__gallery', '.product-main__info', '.cart-line', '.cart-summary',
      '.search-page-form', '.product-description', '.empty-page', 'footer'
    ].join(',');
    const elements = qa(selector, root);
    elements.forEach((element, index) => {
      if (element.classList.contains('reveal-item')) return;
      element.classList.add('reveal-item');
      element.style.setProperty('--reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);
      if (reducedMotion || !revealObserver) element.classList.add('is-visible');
      else revealObserver.observe(element);
    });
  }

  function initRoot(root = document) {
    initCarousels(root);
    initReveal(root);
    renderWishlist();
  }

  async function cartRequest(path, options = {}) {
    const response = await fetch(path, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.method && options.method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Shopify could not update the bag.');
    }
    return response.json();
  }

  function getCart() {
    return cartRequest(window.Sabha.routes.cart + '.js');
  }

  function renderCart(cart) {
    qa('[data-cart-count]').forEach((bubble) => {
      bubble.textContent = cart.item_count;
      bubble.hidden = cart.item_count === 0;
    });

    const drawer = q('[data-cart-drawer]');
    if (!drawer) return;
    const list = q('[data-cart-items]', drawer);
    const footer = q('[data-cart-footer]', drawer);
    if (!list || !footer) return;

    if (!cart.item_count) {
      list.innerHTML = '<div class="drawer-empty"><span>✦</span><h3>Your bag is waiting</h3><p>Discover a handpicked drape for your next celebration.</p></div>';
      footer.hidden = true;
      return;
    }

    list.innerHTML = cart.items.map((item, index) => {
      const title = escapeHtml(item.product_title);
      const variant = escapeHtml(item.variant_title || '');
      const url = escapeHtml(item.url);
      const image = escapeHtml(item.image || '');
      return `
        <article class="drawer-item">
          <a href="${url}"><img src="${image}" alt="" width="164" height="210"></a>
          <div>
            <a href="${url}"><h3>${title}</h3></a>
            <p>${variant}</p>
            <strong>${formatMoney(item.final_line_price)}</strong>
            <div class="drawer-quantity">
              <button data-cart-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <button class="drawer-item__remove" data-cart-change="${index + 1}" data-quantity="0" aria-label="Remove ${title}">×</button>
        </article>`;
    }).join('');

    const total = q('[data-cart-total]', drawer);
    if (total) total.textContent = formatMoney(cart.total_price);
    footer.hidden = false;
  }

  async function refreshCart(open = false) {
    const cart = await getCart();
    renderCart(cart);
    if (open) setOpen(q('[data-cart-drawer]'), true);
  }

  function getWishlist() {
    try {
      const items = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  function saveWishlist(items) {
    localStorage.setItem(wishlistKey, JSON.stringify(items));
    renderWishlist();
  }

  function renderWishlist() {
    const items = getWishlist();
    const handles = new Set(items.map((item) => item.handle));
    qa('[data-wishlist-toggle]').forEach((button) => {
      const saved = handles.has(button.dataset.handle);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Save to wishlist');
    });
    qa('[data-wishlist-count]').forEach((bubble) => {
      bubble.textContent = items.length;
      bubble.hidden = items.length === 0;
    });
    const list = q('[data-wishlist-items]');
    if (!list) return;
    list.innerHTML = items.length
      ? items.map((item) => `
        <article class="drawer-item">
          <a href="${escapeHtml(item.url)}"><img src="${escapeHtml(item.image)}" alt="" width="164" height="210"></a>
          <div><a href="${escapeHtml(item.url)}"><h3>${escapeHtml(item.title)}</h3></a><strong>${escapeHtml(item.price)}</strong></div>
          <button class="drawer-item__remove" data-wishlist-remove="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)}">×</button>
        </article>`).join('')
      : '<div class="drawer-empty"><span>♡</span><h3>No liked pieces yet</h3><p>Tap the heart on any product to keep it here.</p></div>';
  }

  function updateVariant(select) {
    const option = select.options[select.selectedIndex];
    const form = select.closest('form');
    const idInput = q('[name="id"]', form);
    const button = q('[type="submit"]:not(.shopify-payment-button__button)', form);
    if (idInput) idInput.value = option.value;
    if (button) {
      button.disabled = option.dataset.available !== 'true';
      button.textContent = option.dataset.available === 'true' ? 'Add to bag' : 'Sold out';
    }
  }

  async function runSearch(input) {
    const results = q('[data-search-results]');
    if (!results) return;
    const term = input.value.trim();
    if (term.length < 2) {
      results.innerHTML = '';
      return;
    }
    const url = `${window.Sabha.routes.predictiveSearch}.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`;
    try {
      const data = await fetch(url, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      const products = data.resources?.results?.products || [];
      results.innerHTML = products.length
        ? products.map((product) => `
          <a class="search-result" href="${escapeHtml(product.url)}">
            <img src="${escapeHtml(product.image || '')}" alt="" width="128" height="160">
            <div><h3>${escapeHtml(product.title)}</h3><span>${formatMoney(product.price)}</span></div>
          </a>`).join('')
        : '<p>No products found.</p>';
    } catch {
      results.innerHTML = '<p>Search is temporarily unavailable.</p>';
    }
  }

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-open-cart]')) {
      event.preventDefault();
      refreshCart(true).catch((error) => window.alert(error.message));
      return;
    }
    if (target.closest('[data-open-wishlist]')) {
      event.preventDefault();
      renderWishlist();
      setOpen(q('[data-wishlist-drawer]'), true);
      return;
    }
    if (target.closest('[data-open-search]')) {
      event.preventDefault();
      setOpen(q('[data-search-panel]'), true);
      window.setTimeout(() => q('[data-predictive-search-input]')?.focus(), 180);
      return;
    }
    if (target.closest('[data-mobile-menu-open]')) {
      event.preventDefault();
      setOpen(q('[data-mobile-nav]'), true);
      return;
    }
    if (target.closest('[data-close-panel]') || target.matches('.drawer-backdrop')) {
      closePanels();
      return;
    }

    const addButton = target.closest('[data-quick-add]');
    if (addButton) {
      event.preventDefault();
      if (!addButton.dataset.variantId) return;
      const original = addButton.innerHTML;
      addButton.disabled = true;
      addButton.textContent = 'Adding…';
      try {
        await cartRequest(window.Sabha.routes.cartAdd + '.js', {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: Number(addButton.dataset.variantId), quantity: 1 }] })
        });
        addButton.closest('dialog')?.close();
        await refreshCart(true);
      } catch (error) {
        window.alert(error.message);
      } finally {
        addButton.disabled = false;
        addButton.innerHTML = original;
      }
      return;
    }

    const cartChange = target.closest('[data-cart-change]');
    if (cartChange) {
      try {
        const cart = await cartRequest(window.Sabha.routes.cartChange + '.js', {
          method: 'POST',
          body: JSON.stringify({
            line: Number(cartChange.dataset.cartChange),
            quantity: Number(cartChange.dataset.quantity)
          })
        });
        renderCart(cart);
      } catch (error) {
        window.alert(error.message);
      }
      return;
    }

    const wishlistToggle = target.closest('[data-wishlist-toggle]');
    if (wishlistToggle) {
      event.preventDefault();
      const items = getWishlist();
      const itemIndex = items.findIndex((item) => item.handle === wishlistToggle.dataset.handle);
      if (itemIndex >= 0) items.splice(itemIndex, 1);
      else {
        items.push({
          handle: wishlistToggle.dataset.handle,
          title: wishlistToggle.dataset.title,
          url: wishlistToggle.dataset.url,
          image: wishlistToggle.dataset.image,
          price: wishlistToggle.dataset.price
        });
      }
      saveWishlist(items);
      return;
    }

    const wishlistRemove = target.closest('[data-wishlist-remove]');
    if (wishlistRemove) {
      saveWishlist(getWishlist().filter((item) => item.handle !== wishlistRemove.dataset.wishlistRemove));
      return;
    }

    const quickView = target.closest('[data-quick-view]');
    if (quickView) {
      const dialog = q('[data-quick-dialog]');
      const card = quickView.closest('.product-card');
      if (!dialog || !card) return;
      q('[data-qv-image]', dialog).src = card.dataset.image;
      q('[data-qv-title]', dialog).textContent = card.dataset.title;
      q('[data-qv-price]', dialog).textContent = card.dataset.price;
      q('[data-qv-link]', dialog).href = card.dataset.url;
      const dialogAdd = q('[data-qv-add]', dialog);
      dialogAdd.dataset.variantId = card.dataset.variantId;
      dialogAdd.hidden = card.dataset.available !== 'true';
      dialog.showModal();
      return;
    }
    if (target.closest('[data-close-quick-view]')) {
      q('[data-quick-dialog]')?.close();
      return;
    }

    const filterButton = target.closest('[data-filter-button]');
    if (filterButton) {
      qa('[data-filter-button]').forEach((button) => button.classList.toggle('is-active', button === filterButton));
      const filter = filterButton.dataset.filter;
      qa('[data-product-type]').forEach((card) => {
        const visible = filter === 'all' || card.dataset.productType === filter;
        card.hidden = !visible;
        if (visible) {
          card.classList.remove('is-visible');
          requestAnimationFrame(() => card.classList.add('is-visible'));
        }
      });
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target instanceof Element ? event.target.closest('[data-predictive-search-input]') : null;
    if (!input) return;
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => runSearch(input), 220);
  });

  document.addEventListener('change', (event) => {
    const select = event.target instanceof Element ? event.target.closest('[data-product-select]') : null;
    if (select) updateVariant(select);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target instanceof Element ? event.target.closest('[data-product-form]') : null;
    if (!form || event.submitter?.classList.contains('shopify-payment-button__button')) return;
    event.preventDefault();
    const submit = q('[type="submit"]:not(.shopify-payment-button__button)', form);
    const original = submit?.textContent;
    if (submit) {
      submit.disabled = true;
      submit.textContent = 'Adding…';
    }
    try {
      const data = new FormData(form);
      await cartRequest(window.Sabha.routes.cartAdd + '.js', {
        method: 'POST',
        body: JSON.stringify({
          items: [{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }]
        })
      });
      await refreshCart(true);
    } catch (error) {
      window.alert(error.message);
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = original;
      }
    }
  });

  const quickDialog = q('[data-quick-dialog]');
  quickDialog?.addEventListener('click', (event) => {
    if (event.target === quickDialog) quickDialog.close();
  });

  function updateScrollMotion() {
    scrollTicking = false;
    const scrollTop = window.scrollY;
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    document.documentElement.style.setProperty('--scroll-progress', Math.min(1, scrollTop / maxScroll));
    q('.site-header')?.classList.toggle('is-scrolled', scrollTop > 18);
    const hero = q('.hero');
    if (hero) {
      const progress = Math.max(0, Math.min(1, -hero.getBoundingClientRect().top / Math.max(hero.offsetHeight, 1)));
      hero.style.setProperty('--hero-progress', progress);
      hero.style.setProperty('--hero-scroll-shift', `${progress * 16}px`);
      hero.style.setProperty('--hero-content-shift', `${progress * -20}px`);
    }
  }

  function requestScrollMotion() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(updateScrollMotion);
  }

  if (finePointer && !reducedMotion) {
    document.addEventListener('pointermove', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const media = target.closest('.product-card__media');
      if (media) {
        const rect = media.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(rect.width, 1)));
        const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / Math.max(rect.height, 1)));
        media.style.setProperty('--tilt-x', `${(0.5 - y) * 3.2}deg`);
        media.style.setProperty('--tilt-y', `${(x - 0.5) * 4.2}deg`);
        media.style.setProperty('--shine-x', `${x * 100}%`);
        media.style.setProperty('--shine-y', `${y * 100}%`);
      }
      const hero = target.closest('.hero');
      if (hero) {
        const rect = hero.getBoundingClientRect();
        const x = (event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5;
        const y = (event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5;
        hero.style.setProperty('--hero-shift-x', `${x * -9}px`);
        hero.style.setProperty('--hero-shift-y', `${y * -6}px`);
        hero.style.setProperty('--hero-copy-shift-x', `${x * 4}px`);
      }
    }, { passive: true });

    document.addEventListener('pointerout', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
      const media = target.closest('.product-card__media');
      if (media && !media.contains(relatedTarget)) {
        media.style.setProperty('--tilt-x', '0deg');
        media.style.setProperty('--tilt-y', '0deg');
        media.style.setProperty('--shine-x', '50%');
        media.style.setProperty('--shine-y', '45%');
      }
      const hero = target.closest('.hero');
      if (hero && !hero.contains(relatedTarget)) {
        hero.style.setProperty('--hero-shift-x', '0px');
        hero.style.setProperty('--hero-shift-y', '0px');
        hero.style.setProperty('--hero-copy-shift-x', '0px');
      }
    }, { passive: true });
  }

  if ('IntersectionObserver' in window && !reducedMotion) {
    revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -7% 0px' });
  }

  window.addEventListener('scroll', requestScrollMotion, { passive: true });
  window.addEventListener('resize', requestScrollMotion, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) requestScrollMotion();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initRoot(event.target);
    refreshCart(false).catch(() => {});
    requestScrollMotion();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    qa('[data-carousel]', event.target).forEach((carousel) => carousel.sabhaCarousel?.destroy());
  });

  document.addEventListener('shopify:block:select', (event) => {
    event.target.closest('.reveal-item')?.classList.add('is-visible');
  });

  initRoot();
  refreshCart(false).catch(() => {});
  requestScrollMotion();
})();
